import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { DeviceLink } from "../../components/devices/DeviceLink";
import { Alert } from "../../components/ui/Alert";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { FormField } from "../../components/ui/FormField";
import { Modal } from "../../components/ui/Modal";
import { PageHeader } from "../../components/ui/PageHeader";
import { SegmentedFilter } from "../../components/ui/SegmentedFilter";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { type Column, Table } from "../../components/ui/Table";
import { ApiError } from "../../lib/api";
import {
  type NetworkAccessRequestDuration,
  type NetworkAccessRequestRow,
  type NetworkAccessRequestStatus,
  networkAccessRequestsApi,
} from "../../lib/api/network/accessRequests";
import { formatDateTime } from "../../lib/format";
import { formatMessage, t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

const ALLOWED_HOURS = [1, 4, 8, 24, 72, 168];

type StatusFilter = "all" | NetworkAccessRequestStatus;
const STATUS_TABS: StatusFilter[] = [
  "pending",
  "investigating",
  "approved",
  "rejected",
  "expired",
  "cancelled",
  "all",
];

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

function statusTone(s: NetworkAccessRequestStatus): "info" | "success" | "danger" | "neutral" {
  if (s === "pending" || s === "investigating") return "info";
  if (s === "approved") return "success";
  if (s === "rejected" || s === "cancelled") return "danger";
  return "neutral";
}

function formatAge(iso: string): string {
  const created = new Date(iso).valueOf();
  if (Number.isNaN(created)) return "—";
  const delta = Date.now() - created;
  const minutes = Math.floor(delta / 60_000);
  if (minutes < 60) return formatMessage("network.accessRequests.ageMinutes", { count: String(minutes) });
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return formatMessage("network.accessRequests.ageHours", { count: String(hours) });
  const days = Math.floor(hours / 24);
  return formatMessage("network.accessRequests.ageDays", { count: String(days) });
}

export function NetworkAccessRequestsPage() {
  useLocale();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ["network", "access-requests", statusFilter],
    queryFn: () =>
      networkAccessRequestsApi.list({
        status: statusFilter === "all" ? undefined : statusFilter,
        limit: 200,
      }),
  });

  const countTabs = STATUS_TABS.filter((s) => s !== "all");
  const counts = useQueries({
    queries: countTabs.map((s) => ({
      queryKey: ["network", "access-requests", "count", s] as const,
      queryFn: () =>
        networkAccessRequestsApi
          .list({ status: s as NetworkAccessRequestStatus, limit: 1 })
          .then((res) => res.total),
      staleTime: 60_000,
    })),
  });
  const totalCount = useQuery({
    queryKey: ["network", "access-requests", "count", "all"],
    queryFn: () => networkAccessRequestsApi.list({ limit: 1 }).then((res) => res.total),
    staleTime: 60_000,
  });

  const countMap: Record<StatusFilter, number | undefined> = {
    pending: counts[0]?.data,
    investigating: counts[1]?.data,
    approved: counts[2]?.data,
    rejected: counts[3]?.data,
    expired: counts[4]?.data,
    cancelled: counts[5]?.data,
    all: totalCount.data,
  };

  const closeModal = () => setSelectedId(null);
  const refreshList = () =>
    qc.invalidateQueries({ queryKey: ["network", "access-requests"] });

  const columns: Column<NetworkAccessRequestRow>[] = [
    {
      key: "created",
      header: t("network.accessRequests.column.createdAt"),
      render: (r) => (
        <span className="table-cell--datetime">{formatDateTime(r.created_at)}</span>
      ),
    },
    {
      key: "age",
      header: t("network.accessRequests.column.age"),
      render: (r) => (
        <span className="mono" style={{ fontSize: 12 }}>{formatAge(r.created_at)}</span>
      ),
    },
    {
      key: "enrollee",
      header: t("network.accessRequests.column.enrollee"),
      render: (r) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span>{r.enrollee_full_name || "—"}</span>
          <span style={{ color: "var(--color-text-muted)", fontSize: 12 }}>
            {r.enrollee_phone || "—"}
          </span>
        </div>
      ),
    },
    {
      key: "device",
      header: t("network.accessRequests.column.deviceUid"),
      render: (r) => (
        <DeviceLink deviceId={r.device_id} deviceUid={r.device_uid} />
      ),
    },
    {
      key: "duration",
      header: t("network.accessRequests.column.duration"),
      render: (r) =>
        r.requested_duration === "permanent"
          ? t("network.accessRequests.duration.permanent")
          : `${r.requested_hours}h`,
    },
    {
      key: "status",
      header: t("network.accessRequests.column.status"),
      render: (r) => (
        <Badge tone={statusTone(r.status)}>
          {t(`network.accessRequests.status.${r.status}`)}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (r) => (
        <Button variant="secondary" size="sm" onClick={() => setSelectedId(r.id)}>
          {r.status === "pending"
            ? t("network.accessRequests.action.investigate")
            : t("common.view")}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t("network.accessRequests.eyebrow")}
        title={t("network.accessRequests.title")}
        subtitle={t("network.accessRequests.subtitle")}
      />

      <div style={{ marginBottom: 16 }}>
        <SegmentedFilter<StatusFilter>
          value={statusFilter}
          onChange={setStatusFilter}
          options={STATUS_TABS.map((s) => {
            const labelKey = s === "all" ? "network.accessRequests.filter_all" : `network.accessRequests.status.${s}`;
            const count = countMap[s];
            const labelStr = count !== undefined ? `${t(labelKey)} (${count})` : t(labelKey);
            const dot: "allow" | "block" | "unknown" | "default" | undefined =
              s === "pending" || s === "investigating"
                ? "unknown"
                : s === "approved"
                  ? "allow"
                  : s === "rejected" || s === "cancelled"
                    ? "block"
                    : undefined;
            return { value: s, label: labelStr, dot };
          })}
        />
      </div>

      <Card padding="none">
        {list.isLoading && <LoadingState />}
        {list.error && (
          <ErrorState
            titleKey="states.errorTitle"
            message={translateError(list.error)}
          />
        )}
        {list.data && (
          <Table
            rows={list.data.items}
            columns={columns}
            rowKey={(row) => row.id}
            empty={
              <EmptyState
                titleKey="network.accessRequests.emptyTitle"
                bodyKey="network.accessRequests.emptyBody"
              />
            }
          />
        )}
      </Card>

      {selectedId && (
        <InvestigationModal
          requestId={selectedId}
          onClose={closeModal}
          onDecided={() => {
            closeModal();
            refreshList();
          }}
        />
      )}
    </div>
  );
}

function InvestigationModal({
  requestId,
  onClose,
  onDecided,
}: {
  requestId: string;
  onClose: () => void;
  onDecided: () => void;
}) {
  const navigate = useNavigate();
  const inv = useQuery({
    queryKey: ["network", "access-requests", "investigation", requestId],
    queryFn: () => networkAccessRequestsApi.investigation(requestId),
  });
  const [pickedPoolIds, setPickedPoolIds] = useState<Set<string>>(new Set());
  const [duration, setDuration] = useState<NetworkAccessRequestDuration>("permanent");
  const [hours, setHours] = useState<number>(24);
  const [note, setNote] = useState("");
  const [mode, setMode] = useState<"approve" | "reject">("approve");
  const [confirmReject, setConfirmReject] = useState(false);

  const approve = useMutation({
    mutationFn: () =>
      networkAccessRequestsApi.approve(requestId, {
        url_pool_ids: Array.from(pickedPoolIds),
        duration,
        duration_hours: duration === "temporary" ? hours : null,
        note,
      }),
    onSuccess: onDecided,
  });
  const reject = useMutation({
    mutationFn: () => networkAccessRequestsApi.reject(requestId, note),
    onSuccess: () => {
      setConfirmReject(false);
      onDecided();
    },
  });

  const togglePool = (id: string) => {
    setPickedPoolIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const req = inv.data?.request;
  const pools = inv.data?.denied_url_pools ?? [];

  const canSubmit = useMemo(
    () =>
      mode === "reject" ? true : pickedPoolIds.size > 0 && !approve.isPending,
    [mode, pickedPoolIds.size, approve.isPending],
  );

  function promoteToRule() {
    if (!req) return;
    const params = new URLSearchParams();
    const labels = pools
      .filter((p) => pickedPoolIds.has(p.url_pool_id) || pickedPoolIds.size === 0)
      .map((p) => p.value)
      .slice(0, 5)
      .join(", ");
    if (labels) params.set("destination", labels);
    navigate(`/rules/new?${params.toString()}`);
  }

  return (
    <>
    <Modal
      open
      onClose={onClose}
      title={t("network.accessRequests.investigation.title")}
      size="lg"
    >
      {inv.isLoading && <LoadingState />}
      {inv.error && (
        <ErrorState
          titleKey="states.errorTitle"
          message={translateError(inv.error)}
        />
      )}
      {req && (
        <form
          onSubmit={(e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            if (mode === "approve") approve.mutate();
            else setConfirmReject(true);
          }}
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          <Card title={t("network.accessRequests.investigation.contextTitle")}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <strong>{t("network.accessRequests.column.enrollee")}:</strong>{" "}
                {req.enrollee_full_name || "—"}
              </div>
              <div>
                <strong>{t("network.accessRequests.column.phone")}:</strong>{" "}
                {req.enrollee_phone || "—"}
              </div>
              <div>
                <strong>{t("network.accessRequests.column.deviceUid")}:</strong>{" "}
                <DeviceLink deviceId={req.device_id} deviceUid={req.device_uid} />
              </div>
              <div style={{ gridColumn: "1 / span 2" }}>
                <strong>{t("network.accessRequests.column.reason")}:</strong>
                <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{req.reason}</div>
              </div>
              <div>
                <strong>{t("network.accessRequests.column.duration")}:</strong>{" "}
                {req.requested_duration === "permanent"
                  ? t("network.accessRequests.duration.permanent")
                  : `${req.requested_hours}h`}
              </div>
              <div>
                <strong>{t("network.accessRequests.column.status")}:</strong>{" "}
                <Badge tone={statusTone(req.status)}>
                  {t(`network.accessRequests.status.${req.status}`)}
                </Badge>
              </div>
            </div>
          </Card>

          <Card title={t("network.accessRequests.investigation.poolsTitle")}>
            {pools.length === 0 && (
              <p style={{ color: "var(--color-text-muted)" }}>
                {t("network.accessRequests.investigation.poolsEmpty")}
              </p>
            )}
            {pools.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {pools.map((p) => (
                  <label
                    key={p.url_pool_id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: 8,
                      borderRadius: 6,
                      background: pickedPoolIds.has(p.url_pool_id)
                        ? "var(--color-accent-bg, #eff6ff)"
                        : "transparent",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={pickedPoolIds.has(p.url_pool_id)}
                      onChange={() => togglePool(p.url_pool_id)}
                      disabled={req.status !== "pending"}
                    />
                    <code style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
                      {p.value}
                    </code>
                    <Badge tone="neutral">{p.kind}</Badge>
                    <Badge tone={p.vuln_status === "malicious" ? "danger" : "neutral"}>
                      vuln: {p.vuln_status}
                    </Badge>
                    <span style={{ marginLeft: "auto", color: "var(--color-text-muted)", fontSize: 12 }}>
                      {p.blocked_count} {t("network.accessRequests.blockedCountSuffix")}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </Card>

          {req.status !== "pending" && req.decided_at && (
            <Card title={t("network.accessRequests.decisionCardTitle")}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <strong>{t("network.accessRequests.column.status")}:</strong>{" "}
                  <Badge tone={statusTone(req.status)}>
                    {t(`network.accessRequests.status.${req.status}`)}
                  </Badge>
                </div>
                <div>
                  <strong>{t("network.accessRequests.decisionAt")}:</strong>{" "}
                  {formatDateTime(req.decided_at)}
                </div>
                {req.decision_note && (
                  <div style={{ gridColumn: "1 / span 2" }}>
                    <strong>{t("network.accessRequests.field.note")}:</strong>
                    <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>
                      {req.decision_note}
                    </div>
                  </div>
                )}
              </div>
              {req.status === "approved" && (
                <div style={{ marginTop: 12 }}>
                  <Button type="button" variant="secondary" onClick={promoteToRule}>
                    {t("network.accessRequests.promoteToRule")}
                  </Button>
                  <p
                    className="form-field__hint"
                    style={{ marginTop: 6, marginBottom: 0 }}
                  >
                    {t("network.accessRequests.promoteHint")}
                  </p>
                </div>
              )}
            </Card>
          )}

          {req.status === "pending" && (
            <Card title={t("network.accessRequests.investigation.decisionTitle")}>
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <Button
                  type="button"
                  variant={mode === "approve" ? "primary" : "ghost"}
                  onClick={() => setMode("approve")}
                >
                  {t("network.accessRequests.action.approve")}
                </Button>
                <Button
                  type="button"
                  variant={mode === "reject" ? "danger" : "ghost"}
                  onClick={() => setMode("reject")}
                >
                  {t("network.accessRequests.action.reject")}
                </Button>
              </div>

              {mode === "approve" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <FormField label={t("network.accessRequests.field.duration")}>
                    {(id) => (
                      <select
                        id={id}
                        className="input"
                        value={duration}
                        onChange={(e) =>
                          setDuration(e.target.value as NetworkAccessRequestDuration)
                        }
                      >
                        <option value="permanent">
                          {t("network.accessRequests.duration.permanent")}
                        </option>
                        <option value="temporary">
                          {t("network.accessRequests.duration.temporary")}
                        </option>
                      </select>
                    )}
                  </FormField>
                  {duration === "temporary" && (
                    <FormField label={t("network.accessRequests.field.hours")}>
                      {(id) => (
                        <select
                          id={id}
                          className="input"
                          value={hours}
                          onChange={(e) => setHours(Number(e.target.value))}
                        >
                          {ALLOWED_HOURS.map((h) => (
                            <option key={h} value={h}>
                              {h} {t("network.accessRequests.hoursSuffix")}
                            </option>
                          ))}
                        </select>
                      )}
                    </FormField>
                  )}
                </div>
              )}

              <FormField label={t("network.accessRequests.field.note")}>
                {(id) => (
                  <textarea
                    id={id}
                    className="input"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={3}
                  />
                )}
              </FormField>

              {(approve.error || reject.error) && (
                <Alert tone="danger">
                  {translateError(approve.error || reject.error)}
                </Alert>
              )}

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <Button type="button" variant="ghost" onClick={onClose}>
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant={mode === "reject" ? "danger" : "primary"}
                  loading={approve.isPending || reject.isPending}
                  disabled={!canSubmit}
                >
                  {mode === "reject"
                    ? t("network.accessRequests.action.reject")
                    : `${t("network.accessRequests.action.approve")} (${pickedPoolIds.size})`}
                </Button>
              </div>
            </Card>
          )}
        </form>
      )}
    </Modal>
    <ConfirmDialog
      open={confirmReject}
      title={t("network.accessRequests.reject.confirmTitle")}
      body={t("network.accessRequests.reject.confirmBody")}
      confirmLabel={t("network.accessRequests.action.reject")}
      tone="danger"
      loading={reject.isPending}
      onCancel={() => setConfirmReject(false)}
      onConfirm={() => reject.mutate()}
    />
    </>
  );
}
